/* eslint-disable @typescript-eslint/no-empty-function */
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Platform } from '@angular/cdk/platform';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { UmssIconComponent } from '../icon/icon.component';
import { UmssTimePickerClockComponent } from './time-picker-clock/time-picker-clock.component';
import {
  OverlayScrollBehavior,
  OverlayScrollService
} from '../../services/overlay-scroll.service';
import {
  ShortTimeString,
  isValidShortTimeString,
  parseShortTimeString,
  buildShortTimeString
} from '@umss/shared/utils';

@Component({
  selector: 'umss-time-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    UmssIconComponent,
    UmssTimePickerClockComponent
  ],
  templateUrl: './time-picker.component.html',
  styleUrl: './time-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssTimePickerComponent),
      multi: true
    },
    provideNgxMask()
  ]
})
export class UmssTimePickerComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  @HostBinding('style.display') display = 'contents';
  @ViewChild('nativeTimeInput')
  nativeTimeInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('minuteInput') minuteInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('hourInput') hourInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('clockComponent') clockComponentRef?: UmssTimePickerClockComponent;
  @ViewChild('dropdownEl') dropdownEl?: ElementRef<HTMLElement>;
  private elementRef = inject(ElementRef);
  private platform = inject(Platform);
  private overlayScrollService = inject(OverlayScrollService);

  protected readonly isMobile = this.platform.ANDROID || this.platform.IOS;

  private readonly _fallbackId = `umss-time-picker-${Math.random().toString(36).slice(2)}`;
  get inputId(): string {
    return this.id ?? this._fallbackId;
  }

  // Close when clicking outside the entire time-picker component
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isPopoverOpen) return;

    const clickedInside = this.elementRef.nativeElement.contains(
      event.target as Node
    );
    if (!clickedInside) {
      this.isPopoverOpen = false;
      this._onTouched();
      this.onBlur.emit();
      this._unregisterScrollBehavior();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isPopoverOpen) {
      this.calculatePosition();
    }
  }

  /** Internal FormControl drives the masked text input */
  inputControl = new FormControl<string>('', { nonNullable: true });
  private inputSubscription?: { unsubscribe: () => void };

  ngOnInit(): void {
    // Validate and auto-sync use12Hours with showAmPm:
    // - In 24h mode (use12Hours=false), AM/PM is meaningless, so force it off
    // - In 12h mode (use12Hours=true), showAmPm should be true for proper UI
    if (!this.use12Hours && this.showAmPm) {
      console.warn(
        'time-picker: showAmPm=true conflicts with use12Hours=false (24h mode). Disabling AM/PM toggle.'
      );
      this.showAmPm = false;
    }

    this.inputSubscription = this.inputControl.valueChanges.subscribe(
      (value) => {
        this.syncClockFromInput(value);
        const parsed = this.parseToShortTimeString(value);
        this._onChange(parsed);
        this.timeChange.emit(parsed);
      }
    );
  }

  ngOnDestroy(): void {
    this.inputSubscription?.unsubscribe();
    this._unregisterScrollBehavior();
  }

  /** Content */
  @Input() label?: string;
  @Input() placeholder = 'HH:mm';
  @Input() helperText?: string;
  @Input() errorMessage?: string;

  /** Variant */
  @Input() size: 'Mini' | 'Small' | 'Regular' | 'Large' = 'Regular';

  /** State */
  @Input() disabled = false;
  @Input() required = false;
  @Input() error = false;

  /** HTML */
  @Input() id?: string;
  @Input() name?: string;
  @Input() scrollBehavior: OverlayScrollBehavior = 'close';

  /** Popup behaviour */
  @Input() use12Hours = false;
  /** Whether the AM/PM toggle is shown. When use12Hours=true, this controls visibility of AM/PM selector.
   *  When use12Hours=false, this is ignored (24h mode doesn't need AM/PM). */
  @Input() showAmPm = true;
  @Input() requireOkToClose = false;
  /**
   * Whether to show a round clock dial. This is separate from use12Hours/showAmPm:
   * - 'never'    — inputs only, no dial, no toggle button (default).
   * - 'optional' — adds a Show/Hide clock toggle; visibility choice is remembered per-browser.
   *
   * The clock dial adapts to use12Hours setting:
   * - When use12Hours=true: shows only current AM or PM ring (12, 1-11 for AM; 12, 13-23 for PM)
   * - When use12Hours=false: shows full 24h dial with outer (1-12) and inner (0, 13-23) rings
   *
   * The showAmPm toggle controls visibility independently from the clock dial.
   */
  @Input() showClock: 'never' | 'optional' = 'optional';

  /** Programmatic value binding (alternative to using formControl / ngModel) */
  @Input() set value(v: string | null | undefined) {
    if (v && isValidShortTimeString(v)) {
      this.writeValue(v);
    } else {
      this.writeValue(null);
    }
  }

  /** Outputs */
  @Output() timeChange = new EventEmitter<ShortTimeString | null>();
  /** Fires when the user explicitly commits a value (OK, auto-close on minute, Enter).
   *  Does NOT fire on cancel or outside-click dismissal. */
  @Output() committed = new EventEmitter<ShortTimeString | null>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onFocus = new EventEmitter<void>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onBlur = new EventEmitter<void>();

  /** Popover + clock state */
  protected isPopoverOpen = false;
  protected dropdownStyle: Record<string, string> = {};
  protected clockHours = signal(0);
  protected clockMinutes = signal(0);
  protected clockView = signal<'hours' | 'minutes'>('hours');
  /** Whether the round dial is currently shown below the hour/minute inputs. */
  protected clockVisible = signal(false);
  /** Stable display values for the keyboard inputs — set once on open/mode-switch
   *  so Angular's [value] binding never fights the user while they are typing. */
  protected keyboardHourDisplay = signal('');
  protected keyboardMinuteDisplay = signal('');
  private _overlayScrollId?: symbol;
  private _popoverValueAtOpen: ShortTimeString | null = null;
  // Intercepts scroll events at window capture (before the service's document listener)
  // to prevent internal dropdown scrolls from triggering close.
  private _dropdownScrollInterceptor: ((e: Event) => void) | null = null;

  /** AM/PM derived from clockHours: 0-11 → AM, 12-23 → PM */
  protected get currentAmPm(): 'AM' | 'PM' {
    return this.clockHours() < 12 ? 'AM' : 'PM';
  }

  /** CVA implementation */
  private _onChange: (value: ShortTimeString | null) => void = () => {};
  private _onTouched: () => void = () => {};

  writeValue(value: ShortTimeString | null): void {
    const strValue = value ?? '';
    this.inputControl.setValue(strValue, { emitEvent: false });
    if (value && isValidShortTimeString(value)) {
      const { hours, minutes } = parseShortTimeString(value);
      this.clockHours.set(hours);
      this.clockMinutes.set(minutes);
    } else {
      this.clockHours.set(0);
      this.clockMinutes.set(0);
    }
  }

  registerOnChange(fn: (value: ShortTimeString | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.inputControl.disable({ emitEvent: false });
    } else {
      this.inputControl.enable({ emitEvent: false });
    }
  }

  /** Template event handlers */
  onInputFocus(): void {
    if (this.disabled) return;

    if (!this.isMobile) {
      this.clockView.set('hours');
      this.openDropdown();
    }
    this.onFocus.emit();
  }

  onInputBlur(): void {
    if (this.disabled) return;

    // Don't close popover on blur — closing is handled by document:click
    this._onTouched();
    this.onBlur.emit();
  }

  onNativeTimeChange(event: Event): void {
    if (this.disabled) return;

    const input = event.target as HTMLInputElement;
    const value = input.value; // Format: HH:mm

    if (value && value.length === 5) {
      // value is in HH:mm format, which is a valid ShortTimeString
      this.inputControl.setValue(value, { emitEvent: false });
      this.syncClockFromInput(value);
      const parsed = this.parseToShortTimeString(value);
      this._onChange(parsed);
      this.timeChange.emit(parsed);
    } else if (!value) {
      // Clear the value
      this.inputControl.setValue('', { emitEvent: false });
      this.clockHours.set(0);
      this.clockMinutes.set(0);
      this._onChange(null);
      this.timeChange.emit(null);
    }
  }

  onClockIconClick(): void {
    if (this.disabled) return;

    if (this.isMobile) {
      // On mobile, the icon opens the native picker
      this.nativeTimeInputRef?.nativeElement.showPicker?.();
      this.nativeTimeInputRef?.nativeElement.focus();
      return;
    }
    if (this.isPopoverOpen) {
      this.isPopoverOpen = false;
      this._unregisterScrollBehavior();
    } else {
      this.clockView.set('hours');
      this.openDropdown();
    }
  }

  onPopoverClose(): void {
    this._onTouched();
  }

  /** Dial hour selection — keeps the keyboard display in sync since both are visible at once */
  onHourChange(h: number): void {
    this.clockHours.set(h);
    this.keyboardHourDisplay.set(h.toString().padStart(2, '0'));
    const formatted = buildShortTimeString(h, this.clockMinutes());
    if (formatted) {
      this.inputControl.setValue(formatted, {
        emitEvent: !this.requireOkToClose
      });
    }
  }

  /** Dial minute selection — keeps the keyboard display in sync since both are visible at once */
  onMinuteChange(m: number): void {
    this.clockMinutes.set(m);
    this.keyboardMinuteDisplay.set(m.toString().padStart(2, '0'));
    const formatted = buildShortTimeString(this.clockHours(), m);
    if (formatted) {
      this.inputControl.setValue(formatted, {
        emitEvent: !this.requireOkToClose
      });
    }
    if (!this.requireOkToClose) {
      this.committed.emit((formatted as ShortTimeString) ?? null);
      this.isPopoverOpen = false;
      this._unregisterScrollBehavior();
    }
  }

  onClockViewChange(v: 'hours' | 'minutes'): void {
    this.clockView.set(v);
  }

  /** Focusing the hour input drives the dial to its hour-selection ring */
  onHourInputFocus(): void {
    this.clockView.set('hours');
  }

  /** Focusing the minute input drives the dial to its minute-selection ring */
  onMinuteInputFocus(): void {
    this.clockView.set('minutes');
  }

  /** Shows/hides the dial (only relevant when showClock='optional') and remembers the choice. */
  onToggleClockVisibility(): void {
    const next = !this.clockVisible();
    this.clockVisible.set(next);
    this._saveClockVisiblePreference(next);
    setTimeout(() => {
      if (next) {
        this.clockComponentRef?.focusDial();
      } else {
        this.hourInputRef?.nativeElement.focus();
      }
    }, 0);
  }

  /** AM/PM toggle: shifts clockHours into the correct 12h range */
  onAmPmChange(period: 'AM' | 'PM'): void {
    const h = this.clockHours();
    let newH = h;
    if (period === 'PM' && h < 12) {
      newH = h + 12;
    } else if (period === 'AM' && h >= 12) {
      newH = h - 12;
    }
    if (newH === h) return;

    this.clockHours.set(newH);
    this.keyboardHourDisplay.set(newH.toString().padStart(2, '0'));
    const formatted = buildShortTimeString(newH, this.clockMinutes());
    if (formatted) {
      this.inputControl.setValue(formatted, {
        emitEvent: !this.requireOkToClose
      });
    }
  }

  /** Keyboard mode: hour input.
   *  We intentionally do NOT update keyboardHourDisplay here so Angular's
   *  [value] binding stays stable and never overwrites the user's typing.
   */
  onKeyboardHourInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value.trim();
    if (raw === '') return;
    // user cleared the field — wait for next digit
    const h = parseInt(raw, 10);
    if (isNaN(h)) return;

    const clamped = Math.max(0, Math.min(23, h));
    this.clockHours.set(clamped);
    const formatted = buildShortTimeString(clamped, this.clockMinutes());
    if (formatted) {
      this.inputControl.setValue(formatted, {
        emitEvent: !this.requireOkToClose
      });
    }
    // Auto-jump to minute input once the user has typed 2 digits
    if (raw.length === 2) {
      this.minuteInputRef?.nativeElement.focus();
      this.minuteInputRef?.nativeElement.select();
    }
  }

  /** Keyboard mode: minute input.
   *  Same pattern as onKeyboardHourInput.
   */
  onKeyboardMinuteInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value.trim();
    if (raw === '') return;

    const m = parseInt(raw, 10);
    if (isNaN(m)) return;

    const clamped = Math.max(0, Math.min(59, m));
    this.clockMinutes.set(clamped);
    const formatted = buildShortTimeString(this.clockHours(), clamped);
    if (formatted) {
      this.inputControl.setValue(formatted, {
        emitEvent: !this.requireOkToClose
      });
    }
  }

  /** OK button: commits draft values and closes popup */
  onOkClick(): void {
    const formatted = buildShortTimeString(
      this.clockHours(),
      this.clockMinutes()
    );
    if (formatted) {
      this.inputControl.setValue(formatted, { emitEvent: true });
      this.committed.emit(formatted as ShortTimeString);
    } else {
      this._onChange(null);
      this.timeChange.emit(null);
      this.committed.emit(null);
    }
    this.isPopoverOpen = false;
    this._unregisterScrollBehavior();
  }

  /** CANCEL button: reverts to value stored at popup-open time */
  onCancelClick(): void {
    this.writeValue(this._popoverValueAtOpen);
    this._onChange(this._popoverValueAtOpen);
    this.timeChange.emit(this._popoverValueAtOpen);
    this.isPopoverOpen = false;
    this._unregisterScrollBehavior();
  }

  private openDropdown(): void {
    this._popoverValueAtOpen = isValidShortTimeString(this.inputControl.value)
      ? (this.inputControl.value as ShortTimeString)
      : null;
    this.clockView.set('hours');
    this.clockVisible.set(
      this.showClock === 'optional' ? this._loadClockVisiblePreference() : false
    );
    this._initKeyboardDisplayValues();
    this.calculatePosition();
    this.isPopoverOpen = true;
    this._registerScrollBehavior();
    setTimeout(() => {
      this.hourInputRef?.nativeElement.focus();
      this._attachDropdownScrollInterceptor();
    }, 0);
  }

  /** Programmatically opens the popup (e.g. triggered by a sibling field committing).
   *  No-op on mobile or when already open.
   */
  openPopup(): void {
    if (this.disabled || this.isMobile || this.isPopoverOpen) return;

    this.openDropdown();
  }

  /** Snapshots current clock state into the keyboard display signals.
   *  Shows empty strings when there is no committed value yet, so the
   *  keyboard inputs show the placeholder ("--") rather than "00".
   *  Called on popup open and whenever the user switches to keyboard mode.
   */
  private _initKeyboardDisplayValues(): void {
    if (this.hasClockValue) {
      this.keyboardHourDisplay.set(
        this.clockHours().toString().padStart(2, '0')
      );
      this.keyboardMinuteDisplay.set(
        this.clockMinutes().toString().padStart(2, '0')
      );
    } else {
      this.keyboardHourDisplay.set('');
      this.keyboardMinuteDisplay.set('');
    }
  }

  // ─── Clock visibility preference (localStorage, only used when showClock='optional') ───

  private static readonly _CLOCK_VISIBLE_PREF_KEY =
    'umss:time-picker:clock-visible';

  private _saveClockVisiblePreference(visible: boolean): void {
    try {
      localStorage.setItem(
        UmssTimePickerComponent._CLOCK_VISIBLE_PREF_KEY,
        visible ? '1' : '0'
      );
    } catch {
      // localStorage unavailable (private browsing, quota exceeded, etc.)
    }
  }

  /** Defaults to hidden when there is no saved preference. */
  private _loadClockVisiblePreference(): boolean {
    try {
      return (
        localStorage.getItem(
          UmssTimePickerComponent._CLOCK_VISIBLE_PREF_KEY
        ) === '1'
      );
    } catch {
      return false;
    }
  }

  private _registerScrollBehavior(): void {
    this._unregisterScrollBehavior();
    this._overlayScrollId = this.overlayScrollService.register({
      behavior: this.scrollBehavior,
      onFollow: () => this.calculatePosition(),
      onClose: () => {
        this.isPopoverOpen = false;
        this._onTouched();
        this.onBlur.emit();
        this._unregisterScrollBehavior();
      }
    });
  }

  private _unregisterScrollBehavior(): void {
    if (this._overlayScrollId !== undefined) {
      this.overlayScrollService.unregister(this._overlayScrollId);
      this._overlayScrollId = undefined;
    }
    this._detachDropdownScrollInterceptor();
  }

  /** Fires before the service's document-level capture listener (window → document in capture).
   *  Stops propagation for scrolls inside the dropdown so the service never sees them. */
  private _attachDropdownScrollInterceptor(): void {
    if (!this.isPopoverOpen) return;

    const dropdown = this.dropdownEl?.nativeElement;
    if (!dropdown) return;

    this._detachDropdownScrollInterceptor();
    this._dropdownScrollInterceptor = (event: Event) => {
      if (dropdown.contains(event.target as Node)) {
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener('scroll', this._dropdownScrollInterceptor, {
      capture: true
    });
  }

  private _detachDropdownScrollInterceptor(): void {
    if (this._dropdownScrollInterceptor) {
      window.removeEventListener('scroll', this._dropdownScrollInterceptor, {
        capture: true
      });
      this._dropdownScrollInterceptor = null;
    }
  }

  private calculatePosition(): void {
    const host = this.elementRef.nativeElement;
    const trigger = host.querySelector('.input-wrapper');
    if (!trigger) return;

    const rect = (trigger as HTMLElement).getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const GAP = 4;
    const popoverWidth = 288;

    const spaceBelow = viewportHeight - rect.bottom - GAP;
    const spaceAbove = rect.top - GAP;

    const style: Record<string, string> = {
      'min-width': `${rect.width}px`
    };

    // Always cap to available space so overflow-y: auto can kick in when needed
    if (spaceAbove > spaceBelow) {
      style['bottom'] = `${viewportHeight - rect.top + GAP}px`;
      style['max-height'] = `${spaceAbove}px`;
    } else {
      style['top'] = `${rect.bottom + GAP}px`;
      style['max-height'] = `${spaceBelow}px`;
    }

    if (viewportWidth - rect.left < popoverWidth && rect.right > popoverWidth) {
      style['right'] = `${viewportWidth - rect.right}px`;
    } else {
      style['left'] = `${rect.left}px`;
    }

    this.dropdownStyle = style;
  }

  /** True when inputControl has a valid time value.
   *  Used to control the display of the hand in the clock. */
  protected get hasClockValue(): boolean {
    return isValidShortTimeString(this.inputControl.value);
  }

  get iconSize(): string {
    switch (this.size) {
      case 'Mini':
        return '14px';
      case 'Small':
        return '16px';
      case 'Large':
        return '20px';
      default:
        return '18px';
    }
  }

  /** Parses the masked text input to a valid ShortTimeString or null */
  private parseToShortTimeString(value: string): ShortTimeString | null {
    if (!value || value.length < 5) return null;
    return isValidShortTimeString(value) ? (value as ShortTimeString) : null;
  }

  /** Syncs clock state from text input (only when fully valid) */
  private syncClockFromInput(value: string): void {
    if (!isValidShortTimeString(value)) return;

    const { hours, minutes } = parseShortTimeString(value as ShortTimeString);
    this.clockHours.set(hours);
    this.clockMinutes.set(minutes);
  }
}
